import React from 'react';
import { Switch } from '../ui/switch';
import { cn } from '../../lib/utils';

/**
 * Sharing controls for personal email templates and signatures (#955).
 *
 * One home for the visibility select and the people / groups multi-selects, so
 * the Campaign Builder's template panel and Settings → Templates & Signatures
 * cannot drift. The parent owns the values; these components only render them.
 *
 * - `private`: only the owner sees it.
 * - `shared`: the owner plus the chosen people (`shared_with`, user ids) and the
 *   members of the chosen groups (`shared_with_groups`, group ids). Group access
 *   is resolved by the server on every request.
 * - `organization`: admin-only org master (templates only, and only where the
 *   caller passes `allowOrganization`).
 */

export const VISIBILITY_OPTIONS = [
    { value: 'private', label: 'Private' },
    { value: 'shared', label: 'Shared' },
    { value: 'organization', label: 'Org-wide' },
];

// Color-coded visibility badge config (org=violet, shared=amber, private=stone)
export const VISIBILITY_BADGE = {
    organization: { label: 'Org-wide', cls: 'bg-violet-100 text-violet-700' },
    shared: { label: 'Shared', cls: 'bg-amber-100 text-amber-700' },
    private: { label: 'Private', cls: 'bg-stone-100 text-stone-600' },
};

const labelCls = 'block text-xs font-semibold text-stone-500 uppercase tracking-wide mb-1';
const boxCls = 'max-h-40 overflow-y-auto border border-stone-200 rounded-lg p-2 space-y-1';
const rowCls = 'flex items-center gap-2 text-sm text-stone-700 px-1 py-0.5 rounded hover:bg-stone-50 cursor-pointer';

export function VisibilitySelect({ value, onChange, allowOrganization = false, id, labelClassName = labelCls }) {
    return (
        <div>
            <label htmlFor={id} className={labelClassName}>Visibility</label>
            <select
                id={id}
                value={value}
                onChange={e => onChange(e.target.value)}
                data-testid={id}
                className="w-full px-3 py-2 text-sm border border-stone-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-stone-900/20"
            >
                {VISIBILITY_OPTIONS
                    .filter(o => o.value !== 'organization' || allowOrganization)
                    .map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
        </div>
    );
}

const VISIBILITY_DESCRIPTIONS = {
    private: 'Only you can use it.',
    shared: 'You and the people and groups you choose.',
    organization: 'Everyone in the organisation. Admins only.',
};

/**
 * Visibility as radio cards (#970, the signature builder's General tab). Same
 * values as VisibilitySelect. `fixed` shows one tier as read-only (admin
 * signature templates are always Org-wide on the server).
 */
export function VisibilityCards({ value, onChange, allowOrganization = false, fixed = null, idPrefix = 'visibility' }) {
    const options = fixed
        ? VISIBILITY_OPTIONS.filter(o => o.value === fixed)
        : VISIBILITY_OPTIONS.filter(o => o.value !== 'organization' || allowOrganization);
    const current = fixed || value;
    return (
        <div className="space-y-2">
            <span id={`${idPrefix}-label`} className="text-sm font-medium leading-none">Visibility</span>
            <div role="radiogroup" aria-labelledby={`${idPrefix}-label`} className="grid gap-2">
                {options.map(o => {
                    const on = current === o.value;
                    return (
                        <button
                            key={o.value}
                            type="button"
                            role="radio"
                            aria-checked={on}
                            aria-disabled={fixed ? true : undefined}
                            data-testid={`${idPrefix}-${o.value}`}
                            onClick={() => { if (!fixed) onChange(o.value); }}
                            className={cn(
                                'flex items-start gap-3 rounded-md border p-3 text-left transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring',
                                on ? 'border-primary ring-1 ring-primary' : 'border-input hover:bg-accent',
                                fixed && 'cursor-default'
                            )}
                        >
                            <span className="mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center rounded-full border border-primary" aria-hidden="true">
                                {on && <span className="h-2 w-2 rounded-full bg-primary" />}
                            </span>
                            <span>
                                <span className="block text-sm font-medium">{o.label}</span>
                                <span className="mt-0.5 block text-xs text-muted-foreground">
                                    {fixed ? 'Everyone in the organisation. Signature templates are always org-wide.' : VISIBILITY_DESCRIPTIONS[o.value]}
                                </span>
                            </span>
                        </button>
                    );
                })}
            </div>
        </div>
    );
}

function toggle(list, id) {
    return list.includes(id) ? list.filter(x => x !== id) : [...list, id];
}

/**
 * People and group checklists for the "Shared" tier. Renders nothing for any
 * other visibility. `onChange` receives `{ shared_with, shared_with_groups }`.
 *
 * `control="switch"` (#970, the signature builder): two columns of rows with a
 * small switch each, and a one-line help text under each label so both lists
 * start at the same height. The default checkbox lists are unchanged.
 */
export function ShareTargets({
    visibility,
    sharedWith = [],
    sharedWithGroups = [],
    onChange,
    members = [],
    groups = [],
    currentUserId,
    showGroups = true,
    idPrefix = 'share',
    control = 'checkbox',
}) {
    if (visibility !== 'shared') return null;
    const people = members.filter(m => m.user_id !== currentUserId);
    if (control === 'switch') {
        const column = (title, help, items, emptyText, isOn, onToggle, kind) => (
            <div className="min-w-0 space-y-2">
                <div className="space-y-1">
                    <p className="text-sm font-medium leading-none">{title}</p>
                    <p className="truncate text-xs text-muted-foreground">{help}</p>
                </div>
                <div className="max-h-56 overflow-y-auto rounded-md border">
                    {items.length === 0 ? (
                        <p className="px-3 py-2 text-xs text-muted-foreground">{emptyText}</p>
                    ) : items.map(it => (
                        <label key={it.id} className="flex cursor-pointer items-center gap-3 border-b px-3 py-2 text-sm last:border-b-0">
                            <Switch size="sm" checked={isOn(it.id)} onCheckedChange={() => onToggle(it.id)}
                                data-testid={`${idPrefix}-${kind}-${it.id}`} aria-label={`Share with ${it.label}`} />
                            <span className="truncate">{it.label}</span>
                        </label>
                    ))}
                </div>
            </div>
        );
        return (
            <div className={cn('grid gap-4', showGroups && 'sm:grid-cols-2')} data-testid={`${idPrefix}-targets`}>
                {column(
                    `People (${sharedWith.length} selected)`, 'Each person turned on can use it.',
                    people.map(m => ({ id: m.user_id, label: m.name || m.email })), 'No teammates found',
                    id => sharedWith.includes(id),
                    id => onChange({ shared_with: toggle(sharedWith, id), shared_with_groups: sharedWithGroups }),
                    'person',
                )}
                {showGroups && column(
                    `Groups (${sharedWithGroups.length} selected)`, 'Members get access while in the group.',
                    groups.map(g => ({ id: g.group_id, label: g.name })), 'No groups in your organisation',
                    id => sharedWithGroups.includes(id),
                    id => onChange({ shared_with: sharedWith, shared_with_groups: toggle(sharedWithGroups, id) }),
                    'group',
                )}
            </div>
        );
    }
    return (
        <div className="space-y-3" data-testid={`${idPrefix}-targets`}>
            <div>
                <p className={labelCls}>Share with people ({sharedWith.length} selected)</p>
                <div className={boxCls}>
                    {people.length === 0 ? (
                        <p className="text-xs text-stone-400 py-1">No teammates found</p>
                    ) : people.map(m => (
                        <label key={m.user_id} className={rowCls}>
                            <input
                                type="checkbox"
                                checked={sharedWith.includes(m.user_id)}
                                onChange={() => onChange({ shared_with: toggle(sharedWith, m.user_id), shared_with_groups: sharedWithGroups })}
                                className="rounded border-stone-300"
                                data-testid={`${idPrefix}-person-${m.user_id}`}
                            />
                            <span className="truncate">{m.name || m.email}</span>
                        </label>
                    ))}
                </div>
            </div>
            {showGroups && (
                <div>
                    <p className={labelCls}>Share with groups ({sharedWithGroups.length} selected)</p>
                    <div className={boxCls}>
                        {groups.length === 0 ? (
                            <p className="text-xs text-stone-400 py-1">No groups in your organisation</p>
                        ) : groups.map(g => (
                            <label key={g.group_id} className={rowCls}>
                                <input
                                    type="checkbox"
                                    checked={sharedWithGroups.includes(g.group_id)}
                                    onChange={() => onChange({ shared_with: sharedWith, shared_with_groups: toggle(sharedWithGroups, g.group_id) })}
                                    className="rounded border-stone-300"
                                    data-testid={`${idPrefix}-group-${g.group_id}`}
                                />
                                <span className="truncate">{g.name}</span>
                            </label>
                        ))}
                    </div>
                    <p className="text-[10px] text-stone-400 mt-1">
                        Group members get access as long as they are in the group.
                    </p>
                </div>
            )}
        </div>
    );
}
